import { useMemo, useState } from 'react';
import { Download, Printer, RefreshCw, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/ui/Loader';
import { useModuleAccess } from '@/lib/useModuleAccess';
import {
  buildExtensionDirectoryReport,
  downloadHtml,
  openPrintableHtml,
  renderExtensionDirectoryHtml,
  type ExtensionDirectoryReport as ReportData,
} from '@/services/configReports';

export function ExtensionDirectoryReport() {
  const { session } = useModuleAccess('reporting');
  const customerName = session?.customer?.name || 'Customer';
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<ReportData | null>(null);
  const [filter, setFilter] = useState<string>('all');

  const generate = async () => {
    setLoading(true);
    try {
      const data = await buildExtensionDirectoryReport(customerName);
      setReport(data);
      setFilter('all');
      toast.success(
        `Loaded ${data.total} extensions in ${data.groups.length} group${data.groups.length === 1 ? '' : 's'}`
      );
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Failed to build extension directory');
    } finally {
      setLoading(false);
    }
  };

  const visibleGroups = useMemo(() => {
    if (!report) return [];
    if (filter === 'all') return report.groups;
    return report.groups.filter((g) => g.key === filter);
  }, [filter, report]);

  const html = useMemo(
    () => (report ? renderExtensionDirectoryHtml(report) : ''),
    [report]
  );

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-teal-100 dark:bg-teal-900/30 rounded-xl">
              <Users className="w-6 h-6 text-teal-700 dark:text-teal-300" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Extension Directory
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Extensions grouped by PBX extension groups (departments / companies) for{' '}
                {customerName}.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void generate()} isLoading={loading} size="sm">
              <RefreshCw className="w-4 h-4 mr-1.5" />
              {report ? 'Refresh' : 'Generate'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!report}
              onClick={() => {
                if (!report) return;
                downloadHtml(
                  `Extension_Directory_${customerName.replace(/\s+/g, '_')}.html`,
                  html
                );
                toast.success('HTML downloaded');
              }}
            >
              <Download className="w-4 h-4 mr-1.5" />
              Download HTML
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!report}
              onClick={() => {
                if (!html) return;
                openPrintableHtml(html);
              }}
            >
              <Printer className="w-4 h-4 mr-1.5" />
              Print / PDF
            </Button>
          </div>
        </div>
      </Card>

      {loading && (
        <Card>
          <Loader text="Loading extensions, groups, and phones from PBX..." />
        </Card>
      )}

      {!loading && report && (
        <>
          <Card>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setFilter('all')}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-opacity ${
                  filter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:opacity-90'
                }`}
              >
                All · {report.total}
              </button>
              {report.groups.map((group) => (
                <button
                  key={group.key}
                  type="button"
                  onClick={() => setFilter(group.key)}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold text-white transition-opacity ${
                    filter === group.key ? 'ring-2 ring-offset-2 ring-gray-400' : 'opacity-90 hover:opacity-100'
                  }`}
                  style={{ backgroundColor: group.color }}
                >
                  {group.badge} <span className="font-medium opacity-90">· {group.rows.length}</span>
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Generated {new Date(report.generatedAt).toLocaleString()} · Sections follow PBX
              extension-group membership
              {report.ungroupedCount > 0
                ? ` · ${report.ungroupedCount} ungrouped (not in any listed group)`
                : ''}
              . Phone shows mobile number (when set) plus registered handset model or Linkus
              App.
            </p>
          </Card>

          {visibleGroups.map((group) => (
            <Card key={group.key} className="overflow-hidden p-0">
              <div
                className="px-5 py-4 text-white flex flex-wrap items-center gap-3"
                style={{ backgroundColor: group.color }}
              >
                <span className="text-xs font-bold tracking-wide bg-white/20 px-2 py-0.5 rounded">
                  {group.badge}
                </span>
                <h4 className="text-lg font-semibold flex-1">{group.label}</h4>
                <span className="text-sm opacity-90">{group.rows.length} extensions</span>
                {group.phoneHint ? (
                  <span className="text-sm font-medium opacity-95">{group.phoneHint}</span>
                ) : null}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800/80">
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      <th className="px-4 py-3 w-20">Ext</th>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Phone</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {group.rows.map((row) => (
                      <tr
                        key={row.number}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800/40"
                      >
                        <td className="px-4 py-2.5">
                          <span className="inline-block font-semibold tabular-nums bg-indigo-50 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200 px-2 py-0.5 rounded-md">
                            {row.number}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-white">
                          {row.name}
                        </td>
                        <td
                          className={`px-4 py-2.5 break-all ${
                            row.email === '—'
                              ? 'text-gray-400 italic'
                              : 'text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          {row.email}
                        </td>
                        <td
                          className={`px-4 py-2.5 tabular-nums ${
                            row.phone === '—'
                              ? 'text-gray-400 italic'
                              : 'text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          {row.phone}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ))}
        </>
      )}

      {!loading && !report && (
        <Card>
          <p className="text-center text-gray-500 dark:text-gray-400 py-10">
            Click <strong>Generate</strong> to pull the live extension directory, grouped by PBX
            extension groups.
          </p>
        </Card>
      )}
    </div>
  );
}
